import { useState } from "react";
import { MoreHorizontal, PencilLine, Settings, Share2, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FolderNameDialog } from "./folder-name-dialog";
import { FolderSettingsDialog } from "./folder-settings-dialog";
import { ShareDialog } from "@/components/shares/share-dialog";
import { useDeleteFolder, useUpdateFolder } from "@/hooks/use-folders";
import { useAuth } from "@/hooks/use-auth";
import type { Folder } from "@/lib/api-types";
import {
  canCreateShareLink,
  canDeleteFolder as canDeleteFolderRole,
  canManageFolderAccess,
} from "@/lib/api-types";

type Props = {
  folder: Folder;
  projectId: string;
  className?: string;
};

/**
 * Rename / Folder settings / Delete.
 *   - Rename    — all workspace tiers (RENAME_FOLDER gate).
 *   - Settings  — ADMIN + FULL_ACCESS only (MANAGE_FOLDER_ACCESS). Toggles
 *                 restricted + manages the folder ACL.
 *   - Delete    — ADMIN + FULL_ACCESS + EDIT_SHARE (DELETE_FOLDER). Soft-delete
 *                 with cascade; recoverable from trash for 10 days.
 *
 * When menu items are missing due to role, we keep the ··· trigger visible so
 * the row visual weight stays consistent across cards.
 */
export function FolderCardMenu({ folder, projectId, className }: Props) {
  const { role } = useAuth();
  const [renameOpen, setRenameOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  const showShare = canCreateShareLink(role);
  const showSettings = canManageFolderAccess(role);
  const showDelete = canDeleteFolderRole(role);

  async function handleRename(name: string) {
    await updateFolder.mutateAsync({
      folderId: folder.id,
      name,
      projectId,
      parentFolderId: folder.parentFolderId,
    });
  }

  function handleDelete() {
    if (!confirm(`Move "${folder.name}" and everything inside it to Trash?`)) return;
    deleteFolder.mutate({
      folderId: folder.id,
      projectId,
      parentFolderId: folder.parentFolderId,
    });
  }

  return (
    <div
      className={className}
      onClick={(e) => {
        // Menu button lives inside a Link — swallow the click so navigation doesn't fire.
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Folder options"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          {showShare && (
            <DropdownMenuItem onSelect={() => setShareOpen(true)}>
              <Share2 />
              Share…
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
            <PencilLine />
            Rename
          </DropdownMenuItem>
          {showSettings && (
            <DropdownMenuItem onSelect={() => setSettingsOpen(true)}>
              <Settings />
              Folder settings
            </DropdownMenuItem>
          )}
          {showDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={handleDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <FolderNameDialog
        mode="rename"
        initialName={folder.name}
        open={renameOpen}
        onOpenChange={setRenameOpen}
        onSubmit={handleRename}
      />

      {showSettings && (
        <FolderSettingsDialog
          folder={folder}
          projectId={projectId}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        />
      )}

      {showShare && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          items={[{ type: "folder", id: folder.id, name: folder.name }]}
        />
      )}
    </div>
  );
}
